import { z } from "zod";
import { getI18n } from "@/services/i18n";
import { PHONE_REGEX, SIP_URI_REGEX } from "@/utils/validation-patterns";
const $t = getI18n();
// import { $t } from "@/services/i18n";

/**
 * Valida se uma string não está vazia e não contém apenas whitespace
 * Usa o .trim() nativo do Zod para remover espaços automaticamente
 * @param message Mensagem de erro personalizada (opcional)
 * @returns Schema de validação Zod
 */
export const nonEmptyString = (message?: string) => z.string().trim().min(1, message);

/**
 * Valida se uma string não está vazia, não contém apenas whitespace e tem tamanho mínimo
 * @param minLength Tamanho mínimo da string (após trim)
 * @param message Mensagem de erro personalizada (opcional)
 * @returns Schema de validação Zod
 */
export const nonEmptyStringWithMin = (minLength: number, message?: string) =>
  z.string().trim().min(minLength, message);

/**
 * Valida se uma string não está vazia, não contém apenas whitespace e tem tamanho máximo
 * @param maxLength Tamanho máximo da string
 * @param message Mensagem de erro personalizada (opcional)
 * @returns Schema de validação Zod
 */
export const nonEmptyStringWithMax = (maxLength: number, message?: string) =>
  z.string().trim().min(1, message).max(maxLength);

/**
 * Valida se uma string não está vazia, não contém apenas whitespace e está dentro dos limites
 * @param minLength Tamanho mínimo da string (após trim)
 * @param maxLength Tamanho máximo da string
 * @param message Mensagem de erro personalizada (opcional)
 * @returns Schema de validação Zod
 */
export const nonEmptyStringWithLimits = (minLength: number, maxLength: number, message?: string) =>
  z.string().trim().min(minLength, message).max(maxLength);

/**
 * Valida se uma string é uma URL válida
 * Permite strings vazias para campos opcionais
 * @param message Mensagem de erro personalizada (opcional)
 * @returns Schema de validação Zod
 */
export const validUrl = () => z.url().trim();

/**
 * Valida senha atual (legacy) - apenas tamanho mínimo e máximo
 * - Mínimo 8 caracteres
 * - Máximo 64 caracteres
 * - Não permite espaços no início ou fim
 * @returns Schema de validação Zod
 */
export const currentPasswordSchema = z
  .string({
    error: (issue) => {
      if (issue.input === undefined) return $t("validation.field_required");
      return $t("auth:password_validation.invalid" as any);
    },
  })
  .min(1, $t("validation.field_required"))
  .superRefine((val, ctx) => {
    // Validar espaços no início ou fim
    if (val !== val.trim()) {
      ctx.addIssue({
        code: "custom",
        message: $t("auth:password_validation.no_whitespace" as any),
      });
    }

    // Validar tamanho mínimo
    if (val.length < 8) {
      ctx.addIssue({
        code: "custom",
        message: $t("auth:password_validation.min_length" as any),
      });
    }

    // Validar tamanho máximo
    if (val.length > 64) {
      ctx.addIssue({
        code: "custom",
        message: $t("auth:password_validation.max_length"),
      });
    }
  });

/**
 * Valida nome de pessoa
 * - Remove espaços extras
 * - Só permite letras, espaços, acentos e alguns caracteres especiais comuns em nomes
 * - Mínimo 1 caractere após trim
 * @param maxLength Tamanho máximo (opcional)
 * @returns Schema de validação Zod
 */
export const nameSchema = (maxLength?: number) => {
  let schema = z
    .string()
    .trim()
    .min(1, $t("validation.field_required"))
    .regex(/^[a-zA-ZÀ-ÿ\u00f1\u00d1\s\-.'&]+$/, {
      message: $t("validation.name_invalid_characters" as any),
    });

  if (maxLength) {
    schema = schema.max(maxLength);
  }

  return schema;
};

/**
 * Valida nome de organização/empresa
 * - Remove espaços extras
 * - Permite letras, números, espaços, acentos e caracteres especiais comuns
 * - Mínimo 1 caractere após trim
 * @param maxLength Tamanho máximo (opcional)
 * @returns Schema de validação Zod
 */
export const companyNameSchema = (maxLength?: number) => {
  let schema = z
    .string()
    .trim()
    .min(1, $t("validation.field_required"))
    .regex(/^[a-zA-ZÀ-ÿ\u00f1\u00d10-9\s\-.'&]+$/, {
      message: $t("validation.company_name_invalid_characters" as any),
    });

  if (maxLength) {
    schema = schema.max(maxLength);
  }

  return schema;
};

/**
 * Valida senha com critérios de segurança:
 * - Mínimo 12 caracteres
 * - Máximo 64 caracteres
 * - Não permite espaços no início ou fim
 * - Deve conter pelo menos uma letra minúscula, uma maiúscula e um número
 * @returns Schema de validação Zod
 */
export const passwordSchema = z
  .string({
    error: (issue) => {
      if (issue.input === undefined) return $t("validation.field_required");
      return $t("auth:password_validation.invalid");
    },
  })
  .min(1, $t("validation.field_required"))
  .superRefine((val, ctx) => {
    // Validar espaços no início ou fim
    if (val !== val.trim()) {
      ctx.addIssue({
        code: "custom",
        message: $t("auth:password_validation.no_whitespace"),
      });
    }

    // Validar tamanho mínimo
    if (val.length < 12) {
      ctx.addIssue({
        code: "custom",
        message: $t("auth:password_validation.min_length"),
      });
    }

    // Validar tamanho máximo
    if (val.length > 64) {
      ctx.addIssue({
        code: "custom",
        message: $t("auth:password_validation.max_length"),
      });
    }

    // Validar complexidade
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*$/.exec(val)) {
      ctx.addIssue({
        code: "custom",
        message: $t("auth:password_validation.complexity"),
      });
    }
  });

/**
 * Valida número de telefone ou URI SIP
 * - Aceita formato E.164 (+1234567890) ou sem + (1234567890)
 * - Aceita formato SIP URI (user@domain.com)
 * @returns Schema de validação Zod
 */
export const phoneOrSipUriValidator = z.string().refine(
  (value) => {
    return PHONE_REGEX.test(value) || SIP_URI_REGEX.test(value);
  },
  {
    message: $t("sip_trunk.validation.phone_or_uri_format"),
  }
);

/**
 * Identificador usado no header From de chamadas SIP de saída.
 * Aceita E.164 ou um usuário/ramal SIP simples; o host pertence ao campo address.
 */
export const OUTBOUND_CALLER_ID_REGEX = /^(?:\+?\d{2,15}|[\w.-]+)$/;

export const outboundCallerIdValidator = z
  .string()
  .refine((value) => OUTBOUND_CALLER_ID_REGEX.test(value), {
    message: $t("sip_trunk.validation.outbound_caller_id_format"),
  });

/**
 * Valida hostname/IP com porta opcional
 * - Aceita hostname: sip.provider.com
 * - Aceita IP: 192.168.1.1
 * - Aceita com porta: example.com:5060
 * - Rejeita protocolos (http://, sip://, etc)
 * @returns Schema de validação Zod
 */
export const hostnameValidator = z.string().refine(
  (value) => {
    const trimmed = value.trim();
    if (!trimmed) return false;

    // Não pode ter protocolo
    if (trimmed.includes("://")) return false;

    // Deve ter pelo menos um caractere alfanumérico
    // Aceita hostname, IP, com ou sem porta
    return /^[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?(:[0-9]{1,5})?$/.test(trimmed);
  },
  {
    message: $t("sip_trunk.validation.invalid_hostname"),
  }
);
